import { AlertCircle, CheckCircle2, ChevronDown, Clock, CornerDownRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { cn } from '../../../lib/cn';
import { URGENCY_CLASSES, daysLabel, daysSince, urgencyFor } from '../lib/daysSince';
import { usePostThreadMutation } from '../threads.api';
import type { Thread, ThreadTarget } from '../threads.schema';
import { Avatar } from './Avatar';
import { ReplyForm } from './ReplyForm';

interface Props {
    thread: Thread;
    target: ThreadTarget;
    replies?: Thread[];
    resolvedIssueIds: Set<number>;
}

function formatAuthor(thread: Thread): string {
    if (!thread.author) return 'Utilizator necunoscut';
    return thread.author.name || thread.author.email || `User #${thread.userId}`;
}

function formatTime(iso?: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function firstLine(text: string, max = 140): string {
    const oneLine = text.replace(/\s+/g, ' ').trim();
    if (oneLine.length <= max) return oneLine;
    return oneLine.slice(0, max - 1) + '…';
}

export function ThreadItem({ thread, target, replies = [], resolvedIssueIds }: Props) {
    const isRoot = thread.parentId == null;
    const isResolved = isRoot && resolvedIssueIds.has(thread.id);
    const isResolution = thread.params?.resolvesIssueId != null;

    const [expanded, setExpanded] = useState(false);
    const [replying, setReplying] = useState(false);

    const [postThread, { isLoading: isResolving }] = usePostThreadMutation();

    const handleResolve = async () => {
        try {
            await postThread({
                target,
                parentId: thread.id,
                message: 'Marcată ca rezolvată.',
                params: { resolvesIssueId: thread.id },
            }).unwrap();
            toast.success('Problema a fost marcată ca rezolvată');
        } catch (e) {
            const err = e as { message?: string };
            if (err?.message === 'issue_already_resolved') {
                toast.error('Problema este deja rezolvată');
            } else {
                toast.error('Eroare la rezolvare');
            }
        }
    };

    if (!isRoot) {
        return (
            <div
                className={cn(
                    'mt-2 ml-6 flex gap-3 rounded-lg p-3',
                    'bg-gray-50 dark:bg-gray-900/40',
                    isResolution && 'bg-emerald-50 dark:bg-emerald-950/40',
                )}
            >
                <Avatar size="sm" name={thread.author?.name} email={thread.author?.email} />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {formatAuthor(thread)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTime(thread.createdAt)}
                        </span>
                        {isResolution && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                <CheckCircle2 aria-hidden="true" className="h-3 w-3" />
                                rezolvare
                            </span>
                        )}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-800 dark:text-gray-200">
                        {thread.message}
                    </p>
                </div>
            </div>
        );
    }

    const days = !isResolved ? daysSince(thread.createdAt) : 0;
    const urgency = urgencyFor(days);
    const replyCount = replies.length;

    return (
        <div
            className={cn(
                'group overflow-hidden rounded-xl border border-solid bg-white shadow-sm transition-colors',
                'border-gray-200 dark:border-gray-700 dark:bg-gray-800',
                !isResolved && 'border-l-4 border-l-red-500 dark:border-l-red-400',
                isResolved && 'opacity-70 hover:opacity-100',
            )}
        >
            <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                className={cn(
                    'flex w-full items-start gap-3 border-none bg-transparent p-3 text-left',
                    'transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/40',
                )}
            >
                <span
                    aria-hidden="true"
                    className={cn(
                        'mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                        isResolved
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
                    )}
                >
                    {isResolved ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                </span>

                <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {formatAuthor(thread)}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTime(thread.createdAt)}
                        </span>
                        {!isResolved && days > 0 && (
                            <span
                                className={cn(
                                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold',
                                    URGENCY_CLASSES[urgency],
                                )}
                                title="Nerezolvată de"
                            >
                                <Clock aria-hidden="true" className="h-3 w-3" />
                                {daysLabel(days)}
                            </span>
                        )}
                        {replyCount > 0 && (
                            <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                                {replyCount} {replyCount === 1 ? 'răspuns' : 'răspunsuri'}
                            </span>
                        )}
                    </span>
                    {!expanded && (
                        <span className="mt-1 block truncate text-sm text-gray-600 dark:text-gray-300">
                            {firstLine(thread.message)}
                        </span>
                    )}
                </span>

                <span
                    aria-hidden="true"
                    className={cn(
                        'mt-0.5 shrink-0 text-gray-400 transition-transform',
                        expanded && 'rotate-180',
                    )}
                >
                    <ChevronDown className="h-4 w-4" />
                </span>
            </button>

            {expanded && (
                <div className="border-t border-solid border-gray-100 px-3 pb-3 dark:border-gray-700/60">
                    <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                        {thread.message}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setReplying((v) => !v)}
                        >
                            <CornerDownRight aria-hidden="true" className="h-4 w-4" />
                            {replying ? 'Renunță' : 'Răspunde'}
                        </Button>
                        {!isResolved && (
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                disabled={isResolving}
                                onClick={handleResolve}
                                className="ml-auto"
                            >
                                <CheckCircle2 aria-hidden="true" className="h-4 w-4" />
                                Marchează rezolvată
                            </Button>
                        )}
                    </div>

                    {replies.length > 0 && (
                        <div className="mt-3">
                            {replies.map((child) => (
                                <ThreadItem
                                    key={child.id}
                                    thread={child}
                                    target={target}
                                    resolvedIssueIds={resolvedIssueIds}
                                />
                            ))}
                        </div>
                    )}

                    {replying && (
                        <div className="mt-3 ml-6 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
                            <ReplyForm
                                target={target}
                                parentId={thread.id}
                                autoFocus
                                placeholder="Scrie răspunsul..."
                                onPosted={() => setReplying(false)}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
